/* Optional storefront search enhancements. Ordinary search and ordering helpers
 * retain their native behavior. No price/customer records or cart mutations. */
(function (root, factory) {
  const api = factory();
  if (typeof module === "object" && module.exports) module.exports = api;
  if (root) root.SaberSearchEnhancements = api;
  if (root?.document) api.install(root);
})(typeof window === "undefined" ? globalThis : window, function () {
  "use strict";

  const localTerms = Object.freeze({
    "boiler": "water heater", "boilers": "water heaters",
    "calentador de agua": "water heater", "calentadores de agua": "water heaters",
    "chapa": "door lock", "chapas": "door locks",
    "chapa de puerta": "door lock", "cerradura de puerta": "door lock",
    "manija de puerta": "door handle", "manijas de puerta": "door handles",
    "apagador": "light switch", "apagadores": "light switches",
    "foco": "light bulb", "focos": "light bulbs",
    "tomacorriente": "electrical outlet", "tomacorrientes": "electrical outlets",
    "toma corriente": "electrical outlet", "tomas de corriente": "electrical outlets",
    "llave de lavabo": "bathroom faucet", "llave para lavabo": "bathroom faucet",
    "llave de fregadero": "kitchen faucet", "llave para fregadero": "kitchen faucet",
    "cespol": "drain trap", "cespoles": "drain traps",
    "regadera": "shower head", "regaderas": "shower heads",
    "campana de cocina": "range hood", "campanas de cocina": "range hoods",
    "refrigerador": "refrigerator", "refrigeradores": "refrigerators",
    "aire acondicionado": "air conditioner", "minisplit": "mini split",
    "filtro de aire": "air filter", "filtros de aire": "air filters"
  });

  function localAlternative(value) {
    const term = String(value || "").trim();
    if (term.length > 120) return "";
    const normalized = term.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/\s+/g, " ");
    return Object.prototype.hasOwnProperty.call(localTerms, normalized) ? localTerms[normalized] : "";
  }

  function itemNumber(value) {
    const term = String(value || "").trim();
    if (/^\d{3,18}$/.test(term)) return term;
    const match = /^item\s*#\s*([a-z0-9][a-z0-9._-]{2,119})$/i.exec(term);
    return match && /\d/.test(match[1]) ? match[1].toUpperCase() : "";
  }

  function literalRequested(value, href) {
    try {
      const url = new URL(href);
      return url.searchParams.get("ss_local_literal") === "1" && url.searchParams.get("q") === String(value || "").trim();
    } catch (_) { return false; }
  }

  function storedItemNumber(value) {
    const full = String(value || "").trim();
    if (/^\d{3,18}$/.test(full)) return full;
    const segments = full.split("|").map(item => item.trim());
    let item = "", modelSeen = false;
    for (const segment of segments) {
      const match = /^(item|model)\s*#\s*(.+)$/i.exec(segment);
      if (!match) return "";
      if (match[1].toLowerCase() === "item") {
        if (item) return "";
        item = itemNumber("Item #" + match[2]);
        if (!item) return "";
      } else {
        if (modelSeen) return "";
        modelSeen = true;
      }
    }
    return item;
  }

  function safeProductUrl(value, origin) {
    try {
      const url = new URL(value, origin);
      if (url.origin !== origin || url.username || url.password || /%2f|%5c/i.test(url.pathname) ||
          !/^\/(?:[a-z]{2,3}(?:-[a-z0-9]{2,4})?\/)?products\/[^/]+\/?$/i.test(url.pathname)) return null;
      url.search = ""; url.hash = "";
      return url;
    } catch (_) { return null; }
  }

  function validPayload(payload) {
    return payload?.schema === "saber-item-lookup-v1" && payload.performed === true &&
      typeof payload.query === "string" && Number.isInteger(payload.total) && payload.total >= 0 &&
      Number.isInteger(payload.pages) && payload.pages >= 1 && typeof payload.context === "string" &&
      /^[a-f0-9]{32}$/.test(payload.context) && Array.isArray(payload.products);
  }

  function uniqueItemTarget(payloads, entered, origin) {
    const item = itemNumber(entered);
    if (!item || !Array.isArray(payloads) || !payloads.length) return null;
    const contexts = new Set(), matching = new Map(), variantOwners = new Map();
    for (const payload of payloads) {
      if (!validPayload(payload) || payload.pages !== 1 || payload.truncated !== false ||
          payload.total !== payload.products.length || payload.total > 50) return null;
      contexts.add(payload.context);
      for (const product of payload.products) {
        if (!/^\d+$/.test(String(product.id)) || !Array.isArray(product.variants) ||
            product.variantsTruncated !== false || product.variantCount !== product.variants.length ||
            product.variants.length > 250 || !safeProductUrl(product.url, origin)) return null;
        for (const variant of product.variants) {
          if (!/^\d+$/.test(String(variant.id)) || typeof variant.sku !== "string") return null;
          const owner = variantOwners.get(String(variant.id));
          if (owner && owner !== String(product.id)) return null;
          variantOwners.set(String(variant.id), String(product.id));
          if (storedItemNumber(variant.sku) !== item) continue;
          let record = matching.get(String(product.id));
          const url = safeProductUrl(product.url, origin);
          if (!record) matching.set(String(product.id), record = { url, variants: new Set() });
          if (record.url.href !== url.href) return null;
          record.variants.add(String(variant.id));
        }
      }
    }
    if (contexts.size !== 1 || matching.size !== 1) return null;
    const record = [...matching.values()][0];
    if (record.variants.size === 1) record.url.searchParams.set("variant", [...record.variants][0]);
    return record.url;
  }

  function markLocalForm(form, original, mapped) {
    for (const [name, value] of [["ss_local_original", original], ["ss_local_target", mapped]]) {
      let field = form.querySelector('input[name="' + name + '"]');
      if (!field) { field = form.ownerDocument.createElement("input"); field.type = "hidden"; field.name = name; form.append(field); }
      field.value = value;
    }
  }

  function install(window_) {
    const document_ = window_.document;
    if (window_.saberSearchEnhancementsReady) return;
    window_.saberSearchEnhancementsReady = true;
    const script = document_.currentScript;
    const strings = {
      notice: script?.dataset.localNotice || "Searching for __TERM__.",
      original: script?.dataset.localOriginal || "Search instead for __TERM__"
    };
    const pending = new WeakMap(), resuming = new WeakSet();

    function searchUrlFor(form) {
      try {
        const url = new URL(form.action, window_.location.href);
        const native = new URL(window_.routes?.search_url || "/search", window_.location.origin);
        return url.origin === window_.location.origin && url.pathname === native.pathname ? url : null;
      } catch (_) { return null; }
    }

    function constrained(form, action, submitter) {
      if ((form.method || "get").toLowerCase() !== "get" || form.target && form.target !== "_self" ||
          submitter?.hasAttribute("formaction") || submitter?.hasAttribute("formmethod") || submitter?.hasAttribute("formtarget")) return true;
      const pairs = [...new window_.FormData(form).entries(), ...action.searchParams.entries()];
      if (window_.location.pathname === action.pathname) pairs.push(...new URL(window_.location.href).searchParams.entries());
      return pairs.some(([name, value]) => value && (name.startsWith("filter.") || name === "sort_by" ||
        name === "search-filter" || name === "type" && value !== "product" ||
        name.startsWith("options[") && (name !== "options[prefix]" || value !== "last")));
    }

    async function lookup(action, query, signal, productsOnly) {
      const url = new URL(action.pathname, action.origin);
      url.searchParams.set("view", "item-lookup");
      url.searchParams.set("q", query);
      url.searchParams.set("options[prefix]", "last");
      if (productsOnly) url.searchParams.set("type", "product");
      const response = await window_.fetch(url.href, { signal, credentials: "same-origin", cache: "no-store", headers: { Accept: "application/json" } });
      if (!response.ok || response.redirected || new URL(response.url).origin !== action.origin) throw new Error("Native search unavailable");
      const body = await response.text();
      if (new TextEncoder().encode(body).length > 2000000) throw new Error("Native search response too large");
      const payload = JSON.parse(body);
      if (!validPayload(payload) || payload.query !== query) throw new Error("Invalid native search response");
      return payload;
    }

    function resume(form, submitter) {
      resuming.add(form);
      try {
        if (submitter?.form === form && !submitter.disabled) form.requestSubmit(submitter);
        else form.requestSubmit();
      } finally { resuming.delete(form); }
    }

    document_.addEventListener("submit", event => {
      const form = event.target;
      if (!(form instanceof window_.HTMLFormElement) || !form.matches("form.search") || resuming.has(form) || event.defaultPrevented) return;
      const component = form.closest("predictive-search");
      if (component?.allowNativeSubmit || event.submitter?.closest("[data-predictive-search]")) return;
      const input = form.querySelector('input[type="search"][name="q"]'), action = searchUrlFor(form);
      if (!input || !action || constrained(form, action, event.submitter)) return;
      const term = input.value.trim(), item = itemNumber(term), alternative = localAlternative(term);
      const literal = literalRequested(term, window_.location.href);
      if (!item && (!alternative || literal)) return;
      event.preventDefault(); event.stopImmediatePropagation();
      const previous = pending.get(form);
      if (previous?.term === term && !previous.controller.signal.aborted) return;
      previous?.controller.abort();
      const controller = new AbortController(), submitter = event.submitter;
      const startedAt = window_.location.href;
      const job = { controller, term, invalidated: false };
      pending.set(form, job);
      const changed = () => { job.invalidated = true; controller.abort(); };
      form.addEventListener("input", changed); form.addEventListener("change", changed);
      const timer = window_.setTimeout(() => controller.abort(), 1800);
      const current = () => !job.invalidated && pending.get(form) === job && form.isConnected &&
        input.value.trim() === term && window_.location.href === startedAt && searchUrlFor(form)?.href === action.href;
      (async () => {
        let destination = null, mapped = "";
        try {
          if (item) {
            const queries = [...new Set([item, "Item #" + item, "Item # " + item])];
            const payloads = await Promise.all(queries.map(query => lookup(action, query, controller.signal, true)));
            destination = uniqueItemTarget(payloads, term, window_.location.origin);
          } else {
            const original = await lookup(action, term, controller.signal, false);
            if (original.total === 0 && original.pages === 1 && original.truncated === false) {
              const candidate = await lookup(action, alternative, controller.signal, false);
              if (candidate.context === original.context && candidate.total > 0 && candidate.products.length) mapped = alternative;
            }
          }
        } catch (_) { /* Exact/native search remains available on failure or timeout. */ }
        finally {
          window_.clearTimeout(timer);
          form.removeEventListener("input", changed); form.removeEventListener("change", changed);
        }
        if (!current()) return;
        pending.delete(form);
        if (destination) { window_.location.assign(destination.href); return; }
        if (mapped) { markLocalForm(form, term, mapped); input.value = mapped; }
        resume(form, submitter);
      })();
    }, true);

    function showLocalNotice() {
      const url = new URL(window_.location.href), original = url.searchParams.get("ss_local_original"), mapped = url.searchParams.get("ss_local_target");
      if (!original || !mapped || localAlternative(original) !== mapped || url.searchParams.get("q") !== mapped ||
          document_.querySelector("[data-search-local-notice]")) return;
      const container = document_.querySelector(".template-search-header");
      if (!container) return;
      const note = document_.createElement("p"), link = document_.createElement("a"), caption = document_.createElement("span");
      note.className = "ss-search-recovery__tip"; note.dataset.searchLocalNotice = "";
      caption.textContent = strings.notice.replace("__TERM__", mapped);
      url.searchParams.set("q", original); url.searchParams.delete("ss_local_original"); url.searchParams.delete("ss_local_target");
      url.searchParams.delete("page"); url.searchParams.set("ss_local_literal", "1");
      link.href = url.pathname + url.search; link.textContent = strings.original.replace("__TERM__", original);
      note.append(caption, document_.createTextNode(" "), link); container.append(note);
    }
    if (document_.readyState === "loading") document_.addEventListener("DOMContentLoaded", showLocalNotice, { once: true });
    else showLocalNotice();
    document_.addEventListener("shopify:section:load", showLocalNotice);
  }

  return { localTerms, localAlternative, literalRequested, itemNumber, storedItemNumber, safeProductUrl, validPayload, uniqueItemTarget, markLocalForm, install };
});
